import { t } from 'i18next';
import { Color, Vector3 } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { isDefined } from '../../../../../../../utils/variables';
import { CoordinateManager } from '../../../../../../coordinate-manager';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import {
  getCenterCircles,
  moveCircles,
} from '../../../../../../elements/design/design-node/utils/circle';
import { findLineSegmentIdFromIntersection } from '../../../../../../elements/design/design-node/utils/line-segment-node';
import { findPointIdFromIntersection } from '../../../../../../elements/design/design-node/utils/point-node';
import { mathematicalPrecision } from '../../../../../../utilities/math-helpers';
import { ActionName, DesignTool } from '../../../../../design-tool';
import { PickingFace } from '../../../../../picking-face';
import { PickingLine } from '../../../../../picking-line';
import { PickingSphere } from '../../../../../picking-sphere';
import { addAndCommitAction } from '../../utils/actions';
import {
  getConicCursorVertices,
  getLineCursorVertices,
  getLineSegmentConic,
} from '../../utils/curve';
import { combinePoints, createNewPoint } from '../../utils/point';
import { initActionInProgress } from '../general';

export const movePoint = (tool: DesignTool): ContextMenuItem => {
  const intersectedPointId = findPointIdFromIntersection(tool.asset.designNode, tool.intersection);
  const intersectionPoint = tool.intersection?.point.clone();
  const disabled = !isDefined(intersectedPointId) || !isDefined(intersectionPoint);

  return {
    title: t('contextMenu.movePoint', { ns: 'skyviewDesign' }),
    action: () => {
      if (disabled) {
        return;
      }

      const dashedLine = new PickingLine(new Color(0.75, 0.75, 0.75), 'dashed');
      const tempCursor = new PickingSphere(new Color(0.75, 0.75, 0.75));
      tempCursor.position.set(intersectionPoint.x, intersectionPoint.y, intersectionPoint.z);
      const tempLineSegmentCursors: { cursor: PickingLine; update: (moved: Vector3) => void }[] =
        [];
      const tempFaceCursors: PickingFace[] = [];

      // The circles around the point move with it.
      const circleCursor = new PickingLine();
      const circleConics = getCenterCircles(tool.asset.designNode, intersectedPointId)
        .flatMap((circle) => circle.lineSegments)
        .map((id) => getLineSegmentConic(tool.asset.designNode, id, 'world')!);

      const updateCursorPositions = () => {
        if (isDefined(tool.currentPoint)) {
          dashedLine.updateVertex(0, tool.currentPoint);

          const offset = tool.currentPoint.clone().sub(intersectionPoint);
          circleCursor.setVertices(
            circleConics.flatMap((conic) =>
              getConicCursorVertices([
                {
                  start: conic.start.clone().add(offset),
                  control: conic.control.clone().add(offset),
                  end: conic.end.clone().add(offset),
                  weight: conic.weight,
                },
              ]),
            ),
          );

          tempLineSegmentCursors.forEach((x) => {
            x.update(tool.currentPoint!);
          });
          tempFaceCursors.forEach((x) => {
            x.updateVertex(0, tool.currentPoint!);
          });
        }
      };

      initActionInProgress(tool, {
        actionName: ActionName.MovePoint,
        origin: intersectionPoint.clone(),
        originPointId: intersectedPointId,
        initiate: () => {
          const node = tool.asset.designNode.getPointById(intersectedPointId);

          for (const id of node.segments) {
            // Make sure index 0 is the only one we want to update.
            const lineSegment = tool.asset.designNode.getLineSegmentById(id);

            const otherPoints = lineSegment.values.filter((x) => x !== intersectedPointId);

            if (otherPoints.length === 1) {
              const cursor = new PickingLine();
              const other = tool.asset.designNode.getVertexByPointId(otherPoints[0], 'world');
              const movedIsStart = lineSegment.values[0] === intersectedPointId;

              // Curves keep their shape when the point is moved.
              const update = (moved: Vector3) =>
                cursor.setVertices(
                  movedIsStart
                    ? getLineCursorVertices(moved, other, lineSegment.curve)
                    : getLineCursorVertices(other, moved, lineSegment.curve),
                );

              update(intersectionPoint);
              tool.add(cursor);
              tempLineSegmentCursors.push({ cursor, update });
            }
          }

          dashedLine.setNonIndexedVertices([
            intersectionPoint.clone(),
            tool.asset.designNode.getVertexByPointId(intersectedPointId, 'world'),
          ]);

          tool.add(dashedLine);
          tool.add(tempCursor);
          tool.add(circleCursor);
          updateCursorPositions();
        },
        readyToApply: () => true,
        cleanup: () => {
          tempFaceCursors.forEach((x) => tool.remove(x));
          tempLineSegmentCursors.forEach((x) => tool.remove(x.cursor));
          tool.remove(tempCursor);
          tool.remove(dashedLine);
          tool.remove(circleCursor);
        },
        refresh: () => {
          updateCursorPositions();
        },
        apply: () => {
          const newPoint = tool.intersection?.point.clone() ?? tool.currentPoint?.clone();

          if (!isDefined(newPoint)) {
            return;
          }

          const pointCoordinates = CoordinateManager.getInstance().localToProjectCoords(newPoint);
          const targetLineSegmentId = findLineSegmentIdFromIntersection(
            tool.asset.designNode,
            tool.intersection,
          );
          const targetPointId = findPointIdFromIntersection(
            tool.asset.designNode,
            tool.intersection,
          );
          let changes: RevertChange[] = [];

          addAndCommitAction(tool, {
            commit: () => {
              changes = [];

              if (intersectionPoint.distanceTo(newPoint) < mathematicalPrecision) {
                return;
              }

              let otherPointId = targetPointId;
              const designNode = tool.asset.designNode;
              const offset = designNode
                .getLocalPosition(newPoint)
                .sub(designNode.getPointById(intersectedPointId).vertex);

              /**
               * In case the user is snapping towards a line when moving a point
               * 1. Create a new point on the target line
               * 2. Combine the point with the new target point.
               */
              if (isDefined(targetLineSegmentId)) {
                const result = createNewPoint(
                  tool,
                  pointCoordinates,
                  targetLineSegmentId,
                  undefined,
                  tool.asset.designNode.getPointById(intersectedPointId).attributes,
                );

                otherPointId = result.id;
                changes = result.revertChanges;
              }

              changes = [...moveCircles(designNode, intersectedPointId, offset), ...changes];

              if (isDefined(otherPointId)) {
                changes = [
                  ...combinePoints(tool.asset.designNode, otherPointId, intersectedPointId),
                  ...changes,
                ];
              } else {
                changes = [
                  tool.asset.designNode.upsertPoint({
                    id: intersectedPointId,
                    vertex: tool.asset.designNode.getLocalPosition(newPoint),
                  }),
                  ...changes,
                ];
              }

              tool.asset.designNode.refresh();
              tool.asset.refreshMetadata();
            },
            rollback: () => {
              tool.asset.designNode.revert(changes);
              tool.asset.designNode.refresh();
              tool.asset.refreshMetadata();
            },
          });
        },
      });

      tool.contextMenuCallback?.();
    },
    disabled,
    hotkey: 'M',
  };
};
